import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/auth/AuthContext";
import OrdersPanel from "@/pages/OrdersPanel";
import ApplicationsPanel from "@/pages/ApplicationsPanel";
import AdminBookings from "@/pages/AdminBookings";
import AdminAvailability from "@/pages/AdminAvailability";
import { AdminGallery, AdminSettings, AdminPackages } from "@/pages/AdminExtras";

const PRICE_TYPES = [
  { value: "fixed", label: "Fixed price" },
  { value: "per_unit", label: "Per unit" },
  { value: "starting", label: "Starting at" },
  { value: "range", label: "Range" },
  { value: "custom", label: "Custom Quote" },
];

const inputCls =
  "bg-[#0a0a0c] border border-zinc-700 rounded px-2 py-1.5 text-xs text-white focus:border-[#d4af37] outline-none transition-colors font-mono";

function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back, admin.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto mt-24 rounded-2xl p-8 bg-[#121216] gold-border" data-testid="admin-login-form">
      <img src="/logo-emblem.png" alt="DuroTech28 emblem" className="h-16 w-auto mx-auto mb-4" />
      <h1 className="font-display text-2xl uppercase text-white text-center mb-1">Admin Login</h1>
      <p className="text-[10px] text-zinc-500 font-mono text-center mb-6">DuroTech28 pricing dashboard</p>
      <form onSubmit={submit} className="space-y-3">
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input" className={`${inputCls} w-full !py-3`} />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input" className={`${inputCls} w-full !py-3`} />
        {error && <p className="text-red-400 text-xs font-mono" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-btn"
          className="w-full py-3 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity disabled:opacity-60">
          {busy ? "Signing in..." : "Sign In"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-4">
        <span className="flex-1 h-px bg-zinc-800" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">or</span>
        <span className="flex-1 h-px bg-zinc-800" />
      </div>
      <button type="button" data-testid="admin-google-login-btn"
        onClick={() => {
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          const redirectUrl = window.location.origin + '/admin';
          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        }}
        className="w-full py-3 rounded border border-zinc-700 text-zinc-200 font-bold uppercase tracking-wider text-xs hover:border-[#d4af37] hover:text-[#d4af37] transition-colors flex items-center justify-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.4.75 3.44 3.4 1.53 7.26l3.66 2.84C6.06 7.09 8.8 5.04 12 5.04z"/><path fill="#4285F4" d="M23.25 12.26c0-.79-.07-1.54-.2-2.26H12v4.51h6.32c-.27 1.48-1.11 2.72-2.37 3.56l3.62 2.81c2.12-1.96 3.68-4.85 3.68-8.62z" transform="translate(-0.5 0)"/><path fill="#FBBC05" d="M5.19 14.29a7.2 7.2 0 0 1 0-4.58L1.53 6.87a11.26 11.26 0 0 0 0 10.26l3.66-2.84z"/><path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.62-2.81c-1 .68-2.29 1.08-3.84 1.08-3.2 0-5.94-2.05-6.81-4.9l-3.66 2.84C3.44 20.6 7.4 23.25 12 23.25z"/></svg>
        Sign in with Google
      </button>
    </div>
  );
}

function ServiceRow({ svc, onSaved, authHeaders }) {
  const [draft, setDraft] = useState(svc);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(svc);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        price_type: draft.price_type,
        price: draft.price === "" || draft.price == null ? null : Number(draft.price),
        price_max: draft.price_max === "" || draft.price_max == null ? null : Number(draft.price_max),
        unit: draft.unit || null,
        note: draft.note || "",
        checkout_eligible: !!draft.checkout_eligible,
        published: !!draft.published,
      };
      // explicit nulls must be sent: strip only undefined
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const { data } = await axios.put(`${API}/admin/services/${svc.key}`, payload, { headers: authHeaders() });
      onSaved(data);
      toast.success(`Saved: ${data.name}`);
    } catch (err) {
      toast.error("Save failed. You may need to sign in again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${draft.published ? "bg-[#121216] border-zinc-800" : "bg-[#0d0d10] border-zinc-800/50 opacity-60"}`}
      data-testid={`admin-service-row-${svc.key}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[220px] flex-1">
          <p className="text-sm text-zinc-100 font-medium">{svc.name}</p>
          <p className="text-[10px] text-zinc-500 font-mono">{svc.category} · {svc.key}</p>
        </div>
        <select value={draft.price_type} onChange={(e) => set("price_type", e.target.value)}
          data-testid={`admin-type-${svc.key}`} className={inputCls}>
          {PRICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input type="number" min="0" step="1" placeholder="Price" value={draft.price ?? ""}
          onChange={(e) => set("price", e.target.value)}
          data-testid={`admin-price-${svc.key}`} className={`${inputCls} w-24`} />
        {draft.price_type === "range" && (
          <input type="number" min="0" step="1" placeholder="Max" value={draft.price_max ?? ""}
            onChange={(e) => set("price_max", e.target.value)}
            data-testid={`admin-price-max-${svc.key}`} className={`${inputCls} w-24`} />
        )}
        {draft.price_type === "per_unit" && (
          <input type="text" placeholder="unit (e.g. each)" value={draft.unit ?? ""}
            onChange={(e) => set("unit", e.target.value)}
            data-testid={`admin-unit-${svc.key}`} className={`${inputCls} w-28`} />
        )}
        <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400">
          <input type="checkbox" checked={!!draft.published} onChange={(e) => set("published", e.target.checked)}
            data-testid={`admin-published-${svc.key}`} className="accent-[#d4af37]" />
          Live
        </label>
        <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400">
          <input type="checkbox" checked={!!draft.checkout_eligible} onChange={(e) => set("checkout_eligible", e.target.checked)}
            data-testid={`admin-checkout-${svc.key}`} className="accent-[#d4af37]" />
          PayPal
        </label>
        <button type="button" onClick={save} disabled={!dirty || saving} data-testid={`admin-save-${svc.key}`}
          className="px-4 py-1.5 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-40">
          {saving ? "..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function AdminOverview({ setTab }) {
  const { authHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const h = { headers: authHeaders() };
    Promise.all([
      axios.get(`${API}/admin/bookings`, h).catch(() => ({ data: [] })),
      axios.get(`${API}/quotes`, h).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/applications`, h).catch(() => ({ data: [] })),
    ]).then(([b, q, a]) => setStats({
      upcoming: b.data.filter((x) => x.status === "confirmed").length,
      awaiting: b.data.filter((x) => x.status === "awaiting_deposit").length,
      newOrders: q.data.filter((x) => x.status === "new").length,
      paid: q.data.filter((x) => x.status === "paid").length,
      newApps: a.data.filter((x) => x.status === "new").length,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!stats) return <p className="text-zinc-500 font-mono text-xs">Loading...</p>;
  const cards = [
    { label: "Confirmed Appointments", value: stats.upcoming, tab: "appointments" },
    { label: "Awaiting Deposit", value: stats.awaiting, tab: "appointments" },
    { label: "New Quote Requests", value: stats.newOrders, tab: "orders" },
    { label: "Paid Orders", value: stats.paid, tab: "orders" },
    { label: "New Applications", value: stats.newApps, tab: "applications" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="overview-panel">
      {cards.map((c) => (
        <button key={c.label} type="button" onClick={() => setTab(c.tab)} data-testid={`overview-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="rounded-xl bg-[#121216] gold-border p-5 text-left hover:border-[#d4af37]/50 transition-colors">
          <p className="font-display text-3xl text-[#d4af37]">{c.value}</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">{c.label}</p>
        </button>
      ))}
    </div>
  );
}

function Dashboard() {
  const { logout, authHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("calendar") ? "settings" : "overview");

  useEffect(() => {
    const cal = new URLSearchParams(window.location.search).get("calendar");
    if (cal === "connected") toast.success("Google Calendar connected.");
    else if (cal === "error") toast.error("Google Calendar connection failed — please try again.");
    if (cal) window.history.replaceState({}, "", "/admin");
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/services`, { headers: authHeaders() })
      .then((r) => setServices(r.data))
      .catch((err) => {
        if (err.response?.status === 401) logout();
        else toast.error("Could not load services.");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaved = (updated) =>
    setServices((prev) => prev.map((s) => (s.key === updated.key ? updated : s)));

  const categories = [...new Set(services.map((s) => s.category))];

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl uppercase text-white">Dashboard</h1>
          <p className="text-[11px] text-zinc-500 font-mono mt-1">Manage jobs and edit prices, services, and checkout eligibility.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="admin-view-site-link" className="px-4 py-2 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
            View Site
          </Link>
          <button type="button" onClick={logout} data-testid="admin-logout-btn"
            className="px-4 py-2 rounded border border-zinc-700 text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:border-red-400 hover:text-red-400 transition-colors">
            Log Out
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "overview", label: "Overview" },
          { id: "appointments", label: "Appointments" },
          { id: "availability", label: "Hours & Availability" },
          { id: "pricing", label: "Pricing" },
          { id: "orders", label: "Orders" },
          { id: "applications", label: "Applications" },
          { id: "gallery", label: "Gallery" },
          { id: "settings", label: "Settings" },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-2 rounded text-[11px] font-mono uppercase tracking-wider transition-colors ${
              tab === t.id
                ? "bg-[#d4af37] text-black font-bold"
                : "border border-zinc-700 text-zinc-400 hover:border-[#d4af37] hover:text-[#d4af37]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <AdminOverview setTab={setTab} />
      ) : tab === "appointments" ? (
        <AdminBookings />
      ) : tab === "availability" ? (
        <AdminAvailability />
      ) : tab === "gallery" ? (
        <AdminGallery />
      ) : tab === "settings" ? (
        <AdminSettings />
      ) : tab === "orders" ? (
        <OrdersPanel />
      ) : tab === "applications" ? (
        <ApplicationsPanel />
      ) : loading ? (
        <p className="text-zinc-500 font-mono text-xs">Loading...</p>
      ) : (
        <>
        <AdminPackages />
        <div className="space-y-8">
          {categories.map((cat) => (
            <section key={cat}>
              <h2 className="font-display text-lg uppercase tracking-wide text-[#d4af37] mb-3">{cat}</h2>
              <div className="space-y-2">
                {services.filter((s) => s.category === cat).map((s) => (
                  <ServiceRow key={s.key} svc={s} onSaved={onSaved} authHeaders={authHeaders} />
                ))}
              </div>
            </section>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  return (
    <main className="min-h-screen" data-testid="admin-page">
      {user ? <Dashboard /> : <LoginForm />}
    </main>
  );
}
